import { UserRoles } from 'utils/types';
import Auth from 'app/api/Auth';

export enum PermissionType {
    QuoteAction = 'QuoteAction',
    QuoteCancel = 'QuoteCancel',
    QuoteTransfer = 'QuoteTransfer',
    QuoteReinstate = 'QuoteReinstate',
    QuoteGenerateRenewal = 'QuoteGenerateRenewal',
    QuoteRollbackTransaction = 'QuoteRollbackTransaction',
    QuoteSearch = 'QuoteSearch',
    QuoteView = 'QuoteView',
    QuoteEdit = 'QuoteEdit',
    PolicyReset = 'PolicyReset',
    PolicyHoldCover = 'PolicyHoldCover',
    QuoteUnderwritingDetailsView = 'QuoteUnderwritingDetailsView',
    PremiumPaymentInfoView = 'PremiumPaymentInfoView',
    FinanceView = 'FinanceView',
    AdministrationUnblock = 'AdministrationUnblock',
    AdministrationBlUser = 'AdministrationBlUser',
    AdministrationWelcomeEmail = 'AdministrationWelcomeEmail',
    Administration = 'Administration',
    ClaimsHistoryView = 'ClaimsHistoryView',
}

export class Permissions {
    private static rolePermissions = new Map<UserRoles, PermissionType[]>([
        [
            UserRoles.UnderwriterBluepoint,
            [
                PermissionType.QuoteUnderwritingDetailsView,
                PermissionType.QuoteEdit,
                PermissionType.QuoteAction,
                PermissionType.QuoteSearch,
                PermissionType.QuoteView,
                PermissionType.QuoteCancel,
                PermissionType.AdministrationUnblock,
                PermissionType.ClaimsHistoryView
            ],
        ],
        [
            UserRoles.UnderwriterCommercial,
            [
                PermissionType.QuoteUnderwritingDetailsView,
                PermissionType.QuoteEdit,
                PermissionType.QuoteAction,
                PermissionType.QuoteSearch,
                PermissionType.QuoteView,
                PermissionType.QuoteCancel,
                PermissionType.AdministrationUnblock,
                PermissionType.ClaimsHistoryView
            ],
        ],
        [
            UserRoles.UnderwriterDomestic,
            [
                PermissionType.QuoteUnderwritingDetailsView,
                PermissionType.QuoteEdit,
                PermissionType.QuoteAction,
                PermissionType.QuoteSearch,
                PermissionType.QuoteView,
                PermissionType.QuoteCancel,
                PermissionType.AdministrationUnblock,
                PermissionType.ClaimsHistoryView
            ],
        ],
        [
            UserRoles.UnderwriterCyber,
            [
                PermissionType.QuoteUnderwritingDetailsView,
                PermissionType.QuoteEdit,
                PermissionType.QuoteAction,
                PermissionType.QuoteSearch,
                PermissionType.QuoteView,
                PermissionType.QuoteCancel,
                PermissionType.AdministrationUnblock,
                PermissionType.ClaimsHistoryView
            ],
        ],
        [
            UserRoles.UnderwriterFarm,
            [
                PermissionType.QuoteUnderwritingDetailsView,
                PermissionType.QuoteEdit,
                PermissionType.QuoteAction,
                PermissionType.QuoteSearch,
                PermissionType.QuoteView,
                PermissionType.QuoteCancel,
                PermissionType.AdministrationUnblock,
                PermissionType.ClaimsHistoryView
            ],
        ],
        [
            UserRoles.Underwriter,
            [
                PermissionType.QuoteEdit,
                PermissionType.QuoteAction,
                PermissionType.QuoteUnderwritingDetailsView,
                PermissionType.QuoteSearch,
                PermissionType.QuoteView,
                PermissionType.QuoteCancel,
                PermissionType.PremiumPaymentInfoView,
                PermissionType.AdministrationUnblock,
                PermissionType.QuoteGenerateRenewal,
                PermissionType.QuoteRollbackTransaction,
                PermissionType.ClaimsHistoryView
            ],
        ],
        [
            UserRoles.FinanceAdmin,
            [
                PermissionType.QuoteUnderwritingDetailsView,
                PermissionType.FinanceView,
                PermissionType.QuoteSearch,
                PermissionType.QuoteView,
                PermissionType.QuoteCancel,
                PermissionType.QuoteTransfer,
                PermissionType.QuoteReinstate,
                PermissionType.QuoteGenerateRenewal,
                PermissionType.QuoteRollbackTransaction,
                PermissionType.PolicyReset,
                PermissionType.PolicyHoldCover,
                PermissionType.ClaimsHistoryView
            ],
        ],
        [
            UserRoles.SystemAdmin,
            [
                PermissionType.QuoteEdit,
                PermissionType.QuoteAction,
                PermissionType.QuoteCancel,
                PermissionType.QuoteTransfer,
                PermissionType.QuoteReinstate,
                PermissionType.QuoteGenerateRenewal,
                PermissionType.QuoteRollbackTransaction,
                PermissionType.QuoteUnderwritingDetailsView,
                PermissionType.FinanceView,
                PermissionType.QuoteSearch,
                PermissionType.QuoteView,
                PermissionType.Administration,
                PermissionType.AdministrationUnblock,
                PermissionType.AdministrationBlUser,
                PermissionType.AdministrationWelcomeEmail,
                PermissionType.ClaimsHistoryView
            ],
        ],
        [UserRoles.Claims,
            [
                PermissionType.QuoteSearch,
                PermissionType.QuoteView,
                PermissionType.ClaimsHistoryView
            ]
        ],
        [UserRoles.ClaimsYoui,
            [
                PermissionType.QuoteSearch,
                PermissionType.QuoteView,
                PermissionType.ClaimsHistoryView
            ]
        ],
    ]);

    private static claimsOnlyRoles: UserRoles[] = [UserRoles.Claims, UserRoles.ClaimsYoui];

    public static isClaimsOnly = () => {
        const userRoles = Auth.userRoles();
        return userRoles.length > 0 && userRoles.every(role => Permissions.claimsOnlyRoles.includes(role));
    };

    public static hasPermission = (permission: PermissionType) => {
        const userRoles = Auth.userRoles();
        let hasPerm = false;
        for (let role of userRoles) {
            hasPerm = hasPerm || (Permissions.rolePermissions.get(role) ? Permissions.rolePermissions.get(role)!.includes(permission) : false);
        }
        return hasPerm;
    };
}
